import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { isLocalizedPath, localePath, LOCALE_META, type SiteLocale } from '../i18n/siteLocales';
import { Link } from '../i18n/routing';

// Offer the visitor's language on English pages that have a translation. Never redirects:
// crawlers and people who prefer English always get the URL they asked for.
const ARABIC = ['AE', 'SA', 'QA', 'KW', 'OM', 'BH', 'EG', 'JO', 'LB', 'MA', 'DZ', 'TN', 'IQ', 'LY', 'SD', 'YE', 'PS', 'SY'];
const SPANISH = ['ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR'];

const COPY: Record<Exclude<SiteLocale, 'en'>, { prompt: string; action: string; dismiss: string }> = {
  hi: { prompt: 'यह पेज हिन्दी में उपलब्ध है।', action: 'हिन्दी में देखें', dismiss: 'Continue in English' },
  ar: { prompt: 'هذه الصفحة متاحة باللغة العربية.', action: 'عرض بالعربية', dismiss: 'Continue in English' },
  es: { prompt: 'Esta página está disponible en español.', action: 'Ver en español', dismiss: 'Continue in English' },
};

const DISMISS_KEY = 'trainzilla_language_suggestion_dismissed';

function suggestedLocale(): Exclude<SiteLocale, 'en'> | null {
  let country = '';
  let saved = '';
  try {
    country = localStorage.getItem('trainzilla_country') || '';
    saved = localStorage.getItem('trainzilla_language_preference') || '';
  } catch {
    /* private mode */
  }
  if (saved === 'en') return null;
  if (saved === 'hi' || saved === 'ar' || saved === 'es') return saved;
  if (ARABIC.includes(country)) return 'ar';
  if (SPANISH.includes(country)) return 'es';
  if (country === 'IN') return 'hi';
  const browser = (navigator.language || '').slice(0, 2).toLowerCase();
  if (browser === 'hi' || browser === 'ar' || browser === 'es') return browser;
  return null;
}

export default function LanguageSuggestion() {
  const { language, basePath } = useLanguage();
  const [target, setTarget] = useState<Exclude<SiteLocale, 'en'> | null>(null);

  useEffect(() => {
    if (language !== 'en' || !isLocalizedPath(basePath)) return setTarget(null);
    try {
      if (localStorage.getItem(DISMISS_KEY)) return setTarget(null);
    } catch {
      /* private mode */
    }
    // The country comes from the region lookup, which can finish after first paint.
    const pick = () => setTarget(suggestedLocale());
    pick();
    const timer = window.setTimeout(pick, 2500);
    return () => window.clearTimeout(timer);
  }, [language, basePath]);

  if (!target) return null;
  const copy = COPY[target];
  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode */
    }
    setTarget(null);
  };

  return (
    <div
      dir={LOCALE_META[target].rtl ? 'rtl' : 'ltr'}
      lang={target}
      className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-xl rounded-2xl shadow-lg px-4 py-3 flex flex-wrap items-center gap-3 text-sm"
      style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
      role="region"
      aria-label="Language suggestion"
    >
      <span className="flex-1 min-w-[160px]">{copy.prompt}</span>
      <Link
        to={localePath(basePath, target)}
        onClick={() => {
          try {
            localStorage.setItem('trainzilla_language_preference', target);
          } catch {
            /* private mode */
          }
        }}
        className="px-3 py-1.5 rounded-lg font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
      >
        {copy.action}
      </Link>
      <button type="button" onClick={dismiss} className="px-2 py-1.5 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
        {copy.dismiss}
      </button>
      <button type="button" onClick={dismiss} aria-label="Close" className="p-1" style={{ color: 'var(--text-secondary)' }}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
